import React, { FC } from 'react';
import { stringFormat, T } from 'src/shared/helpers';
import styled from 'styled-components';
import AutoAwesomeIcon from 'src/shared/material_icons/image/auto_awesome.svg';
import ImageIcon from 'src/shared/material_icons/image/image.svg';
import VideocamIcon from 'src/shared/material_icons/av/videocam.svg';
import ViewInArIcon from 'src/shared/material_icons/action/view_in_ar.svg';
import CameraAltIcon from 'src/shared/material_icons/image/camera_alt.svg';
import CheckCircleIcon from 'src/shared/material_icons/action/check_circle.svg';
import InfoIcon from 'src/shared/material_icons/action/info.svg';
import ArrowForwardIcon from 'src/shared/material_icons/navigation/arrow_forward.svg';
import { Link } from '../../widgets/buttons';
import { Checkbox } from '../../widgets/checkbox';
import { AdvancedSelect } from '../../widgets/select';
import { Spinner } from '../../widgets/spinner';

// Shared AI Agents Studio brand gradient (see aiStudio components: bottomPanel, model3dLandingView, ...).
const AI_STUDIO_GRADIENT = 'linear-gradient(135deg, #7c3aed 0%, #a855f7 50%, #ec4899 100%)';

// A single credit package option, as produced by ServiceCredits.
interface PackageOption {
    value: number;
    label: string;
    usageFrom: number;
    price: string;
    originalPrice: string | null;
    discountPerc: number;
}

export interface AiStudioNoCreditsModalContentProps {
    // Formatted current balance (e.g. '0,40' / 'Unlimited').
    creditsAvailableLabel: string;
    packageOptions: PackageOption[];
    selectedTier: number | null;
    onSelectTier: (tier: number | null) => void;
    formatPackageOption: (option: PackageOption) => React.ReactNode;
    hasPackages: boolean;
    // Email/auto-refill opt-in (maps to the ServiceCredits auto-refill toggle).
    autoRefillEnabled: boolean;
    onToggleAutoRefill: (checked: boolean) => void;
    onBuy: () => void;
    isProcessing: boolean;
    disabled: boolean;
    // Formatted price of the cheapest package (e.g. '9,00 USD'); null when no packages are available.
    startingAtLabel: string | null;
    learnMoreUrl: string;
}

// Fills the dialog window and caps to its height so the layout can split into a fixed header, a
// scrollable middle, and a pinned action bar — keeping the CTA visible on short screens.
const Root = styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;
    max-height: 100%;
    min-height: 0;
`;

const Header = styled.div`
    flex-shrink: 0;
    background: ${AI_STUDIO_GRADIENT};
    color: #ffffff;
    padding: 22px 32px 20px;
`;

const Badge = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: rgba(255, 255, 255, 0.18);
    border-radius: 20px;
    padding: 5px 12px;
    font-size: 12px;
    font-weight: 600;

    svg {
        width: 14px;
        height: 14px;
        fill: currentColor;
        display: block;
    }
`;

const HeaderTitle = styled.h1`
    margin: 10px 0 6px;
    font-size: 22px;
    font-weight: 700;
    color: #ffffff;
`;

const HeaderDescription = styled.p`
    margin: 0 0 14px;
    font-size: 14px;
    line-height: 1.4;
    color: rgba(255, 255, 255, 0.9);
`;

const Chips = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
`;

const Chip = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 7px;
    background: rgba(255, 255, 255, 0.15);
    border-radius: 8px;
    padding: 7px 12px;
    font-size: 13px;
    font-weight: 500;
    color: #ffffff;

    svg {
        width: 16px;
        height: 16px;
        fill: currentColor;
        display: block;
    }
`;

// Scrollable middle: only this region scrolls when the modal is taller than the viewport.
const ScrollArea = styled.div`
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 18px 32px 14px;
    background: #ffffff;
`;

// Pinned action bar: always visible, never covered by the scroll.
const ActionArea = styled.div`
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 14px 32px 20px;
    background: #ffffff;
    border-top: 1px solid #f1eef8;
`;

const UnlockCard = styled.div`
    background: rgba(124, 58, 237, 0.06);
    border-radius: 10px;
    padding: 15px 18px;
`;

const UnlockTitle = styled.div`
    font-size: 14px;
    font-weight: 700;
    color: #263238;
    margin-bottom: 12px;
`;

const UnlockList = styled.ul`
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

const UnlockItem = styled.li`
    display: flex;
    align-items: flex-start;
    gap: 10px;
    font-size: 13px;
    line-height: 1.4;
    color: #455a64;

    svg {
        width: 18px;
        height: 18px;
        fill: #9333ea;
        display: block;
        flex-shrink: 0;
        margin-top: 1px;
    }
`;

const InfoBox = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 12px;
    border: 1px solid #ece7f7;
    border-radius: 10px;
    padding: 13px 16px;
`;

const InfoIconCircle = styled.div`
    flex-shrink: 0;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: rgba(124, 58, 237, 0.1);
    color: #7c3aed;
    display: flex;
    align-items: center;
    justify-content: center;

    svg {
        width: 18px;
        height: 18px;
        fill: currentColor;
        display: block;
    }
`;

const InfoTextBlock = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

const InfoTitle = styled.div`
    font-size: 13px;
    font-weight: 700;
    color: #263238;
`;

const InfoText = styled.div`
    font-size: 13px;
    line-height: 1.45;
    color: #607d8b;
`;

const AddonLink = styled(Link)`
    display: inline-block;
    margin-top: 12px;
    font-size: 13px;
    font-weight: 500;
`;

const CreditsRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    border: 1px solid #ece7f7;
    border-radius: 10px;
    padding: 13px 16px;

    span {
        font-size: 14px;
        color: #455a64;
    }

    strong {
        font-size: 18px;
        font-weight: 700;
        color: #263238;
    }
`;

const Field = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const FieldLabel = styled.label`
    font-size: 13px;
    font-weight: 600;
    color: #37474f;
`;

const CheckboxRow = styled.div`
    font-size: 13px;
    color: #607d8b;
`;

const BuyButton = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    width: 100%;
    border: none;
    border-radius: 10px;
    padding: 14px 20px;
    background: ${AI_STUDIO_GRADIENT};
    color: #ffffff;
    font-size: 15px;
    font-weight: 600;
    cursor: pointer;
    transition: opacity 0.2s ease-in;

    svg {
        width: 18px;
        height: 18px;
        fill: currentColor;
        display: block;
    }

    &:hover:not(:disabled) {
        opacity: 0.92;
    }

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
`;

const Footer = styled.div`
    text-align: center;
    font-size: 13px;
    color: #78909c;
`;

const ButtonSpinner = styled(Spinner)`
    width: 20px;
    height: 20px;

    & .path {
        stroke: #ffffff;
    }
`;

/**
 * Presentational layout for the product-setup "You're out of AI credits" dialog. All purchase logic
 * (balance, packages, buy mutation, auto-refill) is owned by ServiceCredits and passed down as props,
 * so this component only renders the marketing header and wires the interactive controls.
 */
const AiStudioNoCreditsModalContent: FC<AiStudioNoCreditsModalContentProps> = ({
    creditsAvailableLabel,
    packageOptions,
    selectedTier,
    onSelectTier,
    formatPackageOption,
    hasPackages,
    autoRefillEnabled,
    onToggleAutoRefill,
    onBuy,
    isProcessing,
    disabled,
    startingAtLabel,
    learnMoreUrl
}) => {
    const canBuy = !isProcessing && !disabled && hasPackages && selectedTier !== null;

    return (
        <Root>
            <Header>
                <Badge>
                    <AutoAwesomeIcon />
                    {T._('AI Agents Studio', 'Admin')}
                </Badge>
                <HeaderTitle>{T._("You're out of AI credits", 'Admin')}</HeaderTitle>
                <HeaderDescription>
                    {T._('Unlock the add-on and keep generating stunning content using the products in your catalog — and beyond.', 'Admin')}
                </HeaderDescription>
                <Chips>
                    <Chip>
                        <ImageIcon />
                        {T._('Product images', 'Admin')}
                    </Chip>
                    <Chip>
                        <VideocamIcon />
                        {T._('Video', 'Admin')}
                    </Chip>
                    <Chip>
                        <ViewInArIcon />
                        {T._('3D models', 'Admin')}
                    </Chip>
                    <Chip>
                        <CameraAltIcon />
                        {T._('Product renders', 'Admin')}
                    </Chip>
                </Chips>
            </Header>

            <ScrollArea>
                <InfoBox>
                    <InfoIconCircle>
                        <InfoIcon />
                    </InfoIconCircle>
                    <InfoTextBlock>
                        <InfoTitle>{T._('Keep generating product images', 'Admin')}</InfoTitle>
                        <InfoText>
                            {T._(
                                'To continue creating AI-generated product images, you need the AI Agents Studio add-on and a credit balance. Each generation consumes credits based on output type and resolution.',
                                'Admin'
                            )}
                        </InfoText>
                    </InfoTextBlock>
                </InfoBox>

                <UnlockCard>
                    <UnlockTitle>{T._('The add-on unlocks the entire AI Agents Studio', 'Admin')}</UnlockTitle>
                    <UnlockList>
                        <UnlockItem>
                            <CheckCircleIcon />
                            {T._('Product and variants images for setup', 'Admin')}
                        </UnlockItem>
                        <UnlockItem>
                            <CheckCircleIcon />
                            {T._('Generate AI content from your catalog or from scratch', 'Admin')}
                        </UnlockItem>
                        <UnlockItem>
                            <CheckCircleIcon />
                            {T._('All AI Agents tools, with no feature limits', 'Admin')}
                        </UnlockItem>
                    </UnlockList>
                    {learnMoreUrl && (
                        <AddonLink href={learnMoreUrl} target='_blank'>
                            {T._('Discover AI Agents Studio here', 'Admin')}
                        </AddonLink>
                    )}
                </UnlockCard>

                <CreditsRow>
                    <span>{T._('Credits available', 'Admin')}</span>
                    <strong>{creditsAvailableLabel}</strong>
                </CreditsRow>

                <Field>
                    <FieldLabel>{T._('Choose a credit package', 'Admin')}</FieldLabel>
                    <AdvancedSelect
                        isSearchable={false}
                        menuPosition={'fixed'}
                        menuPlacement={'auto'}
                        isDisabled={isProcessing || disabled || !hasPackages}
                        options={packageOptions}
                        value={packageOptions.find((option) => option.value === selectedTier) || null}
                        formatOptionLabel={formatPackageOption}
                        onChange={(val: PackageOption | null) => onSelectTier(val?.value ?? null)}
                        placeholder={T._('Select a package...', 'Admin')}
                    />
                </Field>

                <CheckboxRow>
                    <Checkbox
                        checked={autoRefillEnabled}
                        onChange={onToggleAutoRefill}
                        disabled={isProcessing || disabled}
                        label={T._('Email me when credits drop below 20% and when they run out, so you can top up in time.', 'Admin')}
                    />
                </CheckboxRow>
            </ScrollArea>

            <ActionArea>
                <BuyButton type='button' disabled={!canBuy} onClick={onBuy}>
                    {isProcessing ? (
                        <ButtonSpinner />
                    ) : (
                        <>
                            {T._('Buy credits and unlock the Studio', 'Admin')}
                            <ArrowForwardIcon />
                        </>
                    )}
                </BuyButton>

                <Footer>
                    {startingAtLabel ? stringFormat(T._('Starting at {0}.', 'Admin'), startingAtLabel) : null} {T._('Discount if you buy more.', 'Admin')}{' '}
                    {learnMoreUrl && (
                        <Link href={learnMoreUrl} target='_blank'>
                            {T._('Learn more', 'Admin')}
                        </Link>
                    )}
                </Footer>
            </ActionArea>
        </Root>
    );
};

export default AiStudioNoCreditsModalContent;
